import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ISbStories, ISbStoriesParams, ISbStory, ISbStoryData, ISbStoryParams } from '@storyblok/js';
import { Observable, catchError, delay, forkJoin, map, of, switchMap, take, tap, throwError } from 'rxjs';
import { ISbComponentType, ISbLinkURLObject } from 'storyblok-js-client';
import { defaultLanguage, languages } from '../consts';
import { extractLocaleFromPath } from '../lib/locale-extract';
import { STORYBLOK_CACHE_TAGS, StoryblokStoriesQuery, recordStoryblokListTags } from '../lib/storyblok-cache-tags';
import { PicflowStoryblok } from '../picflow-storyblok';
import { localeToPathPrefix } from '../pipes/translate-link.pipe';
import { mapStoryblokLang } from '../storyblok/lib/lang-map';
import { StoryblokLink } from '../storyblok/storyblok-types';
import { LanguageService } from './language.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { TransferStateService } from './transfer-state.service';

@Injectable({
  providedIn: 'root',
})
export class StoryblokService implements PicflowStoryblok {
  private http = inject(HttpClient);
  private dataLoader = inject(ObservableDataLoaderService);
  private transferService = inject(TransferStateService);
  private languageService = inject(LanguageService);
  private cacheTags = inject(STORYBLOK_CACHE_TAGS, { optional: true });
  private publishableKey = 'qit3qOMDswj7KNAg95r4yAtt';
  private readonly linkMap: { [language: string]: Map<string, ISbLinkURLObject> } = {};

  private getCacheVersionAndLanguage() {
    return forkJoin([
      this.transferService
        .withStateTransferObservable('storyblokCV', () =>
          this.dataLoader.get('storyblokCV', () =>
            this.http
              .get<{
                cv: string;
              }>(`https://api.storyblok.com/v2/cdn/stories?content_type=GET_CV&token=${this.publishableKey}`)
              .pipe(map(res => res.cv))
          )
        )
        .pipe(take(1)),
      this.languageService.currentLanguage$.pipe(take(1)),
    ]);
  }

  private getStoryUrl(slug: string, params: ISbStoryParams, cv: string) {
    const query = new URLSearchParams();
    if (params.language && !slug.startsWith('technical/')) {
      query.set('language', params.language);
    }

    if (params.find_by) {
      query.set('find_by', params.find_by);
    }

    return `https://api.storyblok.com/v2/cdn/stories/${slug}?${query.toString()}&token=${this.publishableKey}&version=${
      params.version || 'published'
    }&cv=${cv}&resolve_links=url`;
  }

  private getStoryObservable(
    slug: string,
    params: ISbStoryParams,
    retryCount = 0
  ): Observable<ISbStory['data'] | false> {
    if (!slug || slug.includes('.')) return of(false); // fixes some localhost issues where the browser tries to fetch js files etc.
    return this.getCacheVersionAndLanguage().pipe(
      switchMap(([cv, language]) => {
        const cacheKey = JSON.stringify({ slug, ...params, language });
        return this.dataLoader.get(cacheKey, () => {
          return this.http.get<ISbStory['data']>(this.getStoryUrl(slug, { ...params, language }, cv)).pipe(
            catchError(err => {
              if (err instanceof HttpErrorResponse) {
                if (err.status === 429) {
                  if (retryCount < 3) {
                    const waitTime = Math.pow(2, retryCount) * 1000;
                    return of(null).pipe(
                      delay(waitTime),
                      switchMap(() => this.getStoryObservable(slug, params, retryCount + 1))
                    );
                  }
                }
              }
              return <Observable<undefined>>of(false);
            }),
            map(res => res || (false as const)),
            tap(resp => {
              if (resp) {
                const lang = mapStoryblokLang(resp.story.lang);
                if (!this.linkMap[lang]) this.linkMap[lang] = new Map<string, ISbLinkURLObject>();
                resp.links.forEach(link => {
                  this.linkMap[lang].set(link.uuid, link as ISbLinkURLObject);
                });
              }
            })
          );
        });
      })
    );
  }

  public getStory(slug: string): Observable<ISbStory['data'] | false> {
    if (!slug) return of(false);

    const params: ISbStoryParams = {};
    return this.getStoryObservable(slug, params).pipe(
      tap(res => {
        if (!res) return;
        if (res.story?.id) this.cacheTags?.addItem(res.story.id);
      }),
      take(1)
    );
  }

  private buildStoriesUrl(params: ISbStoriesParams, cv: string) {
    const query = new URLSearchParams();
    if (params.language) {
      query.set('language', params.language);
    }
    if (params.content_type) {
      query.set('content_type', params.content_type);
    }
    if (params.starts_with) {
      query.set('starts_with', params.starts_with);
    }
    if (params.with_tag) {
      query.set('with_tag', params.with_tag);
    }
    if (params.page) {
      query.set('page', params.page.toString());
    }
    if (params.per_page) {
      query.set('per_page', params.per_page.toString());
    }
    if (params.excluding_fields) {
      query.set('excluding_fields', params.excluding_fields);
    }
    if (params.by_uuids) {
      query.set('by_uuids', params.by_uuids);
    }
    if (params.excluding_ids) {
      query.set('excluding_ids', params.excluding_ids);
    }
    if (params.sort_by) {
      query.set('sort_by', params.sort_by);
    }

    if (params.filter_query) {
      params.filter_query.forEach(item => {
        query.set(`filter_query[${item.key}][in]`, item.value);
      });
    }

    return `https://api.storyblok.com/v2/cdn/stories?${query.toString()}&token=${this.publishableKey}&version=${
      params.version || 'published'
    }&cv=${cv}&resolve_links=url`;
  }

  private getAllStoriesInternal(url: string, retryCount = 0): Observable<HttpResponse<ISbStories['data']>> {
    return this.http.get<ISbStories['data']>(url, { observe: 'response', transferCache: false }).pipe(
      map(resp => resp),
      catchError(err => {
        if (err.status === 429) {
          if (retryCount < 3) {
            const waitTime = Math.pow(2, retryCount) * 1000;
            return of(null).pipe(
              delay(waitTime),
              switchMap(() => this.getAllStoriesInternal(url, retryCount + 1))
            );
          }
        }
        return throwError(() => err);
      })
    );
  }

  private getAllStories(
    params: ISbStoriesParams,
    page = 1,
    stories = []
  ): Observable<{
    stories: ISbStoryData[];
    total: number;
    links: (
      | ISbStoryData<
          ISbComponentType<string> & {
            [index: string]: unknown;
          }
        >
      | ISbLinkURLObject
    )[];
  }> {
    const resultsPerPage = 25;
    return this.getCacheVersionAndLanguage().pipe(
      switchMap(([cv]) => {
        const url = this.buildStoriesUrl({ per_page: resultsPerPage, page, ...params }, cv);
        return this.transferService
          .withStateTransferObservable(url, () => {
            return this.getAllStoriesInternal(url).pipe(
              map(_resp => {
                const total = Number(_resp.headers.get('total')) || 0;
                const resp = _resp.body;
                return {
                  stories: resp.stories,
                  total,
                  links: resp.links,
                };
              })
            );
          })
          .pipe(
            switchMap(resp => {
              const lang = mapStoryblokLang(resp.stories[0]?.lang || params.language);
              if (!this.linkMap[lang]) this.linkMap[lang] = new Map<string, ISbLinkURLObject>();
              resp.links.forEach(link => {
                this.linkMap[lang].set(link.uuid, link as ISbLinkURLObject);
              });

              stories = [...stories, ...resp.stories];

              if (stories.length < resp.total && !params.per_page) {
                return this.getAllStories(params, page + 1, stories);
              }
              return of({ stories, total: resp.total, links: resp.links });
            })
          );
      })
    );
  }

  public getStories(options: StoryblokStoriesQuery): Observable<{ stories: ISbStoryData[]; total: number }> {
    const { cache_tagging, ...storyParams } = options;
    return this.getCacheVersionAndLanguage().pipe(
      take(1),
      switchMap(([, language]) => {
        const cacheKey = JSON.stringify({ ...storyParams, language });

        return this.dataLoader
          .get(cacheKey, () =>
            this.getAllStories({ ...storyParams, language }).pipe(
              catchError(err => {
                console.error('Error loading stories', err);
                return of({ stories: [], total: 0 });
              })
            )
          )
          .pipe(
            tap(resp =>
              recordStoryblokListTags(this.cacheTags, { ...storyParams, cache_tagging }, resp?.stories ?? [])
            ),
            take(1)
          );
      })
    );
  }

  public getLinkName(link: StoryblokLink): string | undefined {
    if (!link || link.linktype !== 'story' || !link.id) return undefined;
    const currentLanguage = this.languageService.currentLanguage();
    const linkMapItem = this.linkMap[currentLanguage]?.get(link.id) as {
      name?: string;
      translated_name?: string;
    };
    return linkMapItem?.translated_name || linkMapItem?.name;
  }

  public getUrlLink(link: StoryblokLink) {
    if (!link) return;
    const url = (() => {
      if (!link) return '';
      const currentLanguage = this.languageService.currentLanguage();

      const cachedUrl = link.cached_url // add starting slash if missing (its missing for default language links)
        ? link.cached_url.startsWith('/')
          ? link.cached_url
          : '/' + link.cached_url
        : '';

      if (link.linktype === 'email') {
        return `mailto:${(link.url || link.cached_url).replace(/^mailto:/, '')}`;
      }

      if (link.linktype === 'url') {
        const isPassthrough =
          link.url.includes('://') ||
          link.url.startsWith('mailto:') ||
          link.url.startsWith('tel:') ||
          link.url.startsWith('#');
        if (isPassthrough) return link.url;

        // For templated pages, like compare and convert, we can't use story link types, so instead we assume that all links to these pages should have the language prefix
        // 'convert' kept for legacy /convert/* links in CMS content (301 to /tools/convert/*)
        const programmaticPrefixes = [
          { prefix: 'compare', key: 'compare' },
          { prefix: 'tools/convert', key: 'convert' },
          { prefix: 'convert', key: 'convert' },
        ] as const;
        const shouldAddLanguagePrefix = programmaticPrefixes.some(({ prefix, key }) => {
          if (!cachedUrl.startsWith(`/${prefix}/`) && cachedUrl !== `/${prefix}`) return false;
          const langData = languages.find(l => l.lang === currentLanguage);
          if (!langData) return false;
          return langData.programaticPages[key];
        });
        const linkContainsLocale = !!extractLocaleFromPath(cachedUrl.split('/').filter(Boolean));
        if (shouldAddLanguagePrefix && !linkContainsLocale) {
          const lang = currentLanguage;
          if (lang !== defaultLanguage) {
            return `/${lang}${cachedUrl}`;
          }
        }

        return cachedUrl;
      }

      if (link.linktype === 'story' && cachedUrl && link.id) {
        const linkMapForLang = this.linkMap[currentLanguage];
        const linkMapItem = linkMapForLang?.get(link.id);
        if (!linkMapForLang || !linkMapItem) {
          // Untranslated story — strip the language prefix so the link points to the default-language URL
          // instead of a non-existent translated page (e.g. /de/blog/… → /blog/…)
          if (currentLanguage !== defaultLanguage && cachedUrl.startsWith(`/${currentLanguage}/`)) {
            return cachedUrl.replace(`/${currentLanguage}/`, '/');
          }
          return cachedUrl;
        }

        if (linkMapItem['translated_name'] !== undefined) {
          // only links that have a translated name (using the translated paths feature in storyblok)
          // are respected. Other links fall back to the default language
          const prefix = localeToPathPrefix(this.languageService.currentLocale());
          const linkPrefix = linkMapItem.full_slug.split('/')[0];
          // We need to remove the prefix and add our own, because the full_slug always contains the original language prefix
          // and we need to replace it with the current locale prefix (which could include location as well)
          const linkWithoutPrefix = linkMapItem.full_slug.replace(`${linkPrefix}/`, '');
          return `/${prefix}/${linkWithoutPrefix}`;
        }

        return `/${linkMapItem.url}`;
      }
      return '';
    })()
      .replace(/\/home$/, '/') // Replacing /home with /
      .replace(/(.+)\/$/, '$1'); // Remove trailing slash

    if (link.anchor) return url + '#' + link.anchor;
    return url;
  }
}
